/**
 * Conversation Service
 *
 * Manages chat conversations (1-to-1 DMs and group chats).
 * Uses Supabase RLS for security - all queries are filtered by user access.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import {
  batchGetProfilePhotoSignedUrls,
  collectParticipantProfilePhotoPaths,
  enrichParticipantsWithSignedUrls,
} from '@kit/shared/storage';
import type { Database } from '@kit/supabase/database';

import type {
  ConversationWithDetails,
  CreateGroupConversationParams,
} from '../../types';

export class ConversationService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Get all conversations for the current user
   * Sorted by last message time (most recent first)
   */
  async getConversations(): Promise<Result<ConversationWithDetails[]>> {
    const { data, error } = await this.client
      .from('conversations')
      .select(
        `
        *,
        participants:conversation_participants(
          *,
          profile:profiles!conversation_participants_user_id_profiles_fkey(id, first_name, last_name, profile_photo_url)
        )
      `,
      )
      .is('deleted_at', null)
      .order('last_message_at', { ascending: false, nullsFirst: false });

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    // Get current user ID to find my participation
    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) {
      return Result.fail(
        new ServiceError('UNAUTHORIZED', 'User not authenticated'),
      );
    }

    // Collect all profile photo paths from participants
    const allProfilePhotoPaths = (data || []).flatMap((conv) =>
      collectParticipantProfilePhotoPaths(conv.participants || []),
    );

    // Generate signed URLs in batch
    const signedUrlMap = await batchGetProfilePhotoSignedUrls(
      this.client,
      allProfilePhotoPaths,
    );

    // Transform to ConversationWithDetails with signed URLs
    const conversations = (data || []).map(
      (conv) =>
        ({
          ...conv,
          participants: enrichParticipantsWithSignedUrls(
            conv.participants || [],
            signedUrlMap,
          ),
          my_participation:
            conv.participants?.find(
              (p: { user_id: string }) => p.user_id === user.id,
            ) || null,
        }) as ConversationWithDetails,
    );

    return Result.ok(conversations);
  }

  /**
   * Get a single conversation with full details
   */
  async getConversation(
    conversationId: string,
  ): Promise<Result<ConversationWithDetails>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data, error } = await this.client
      .from('conversations')
      .select(
        `
        *,
        participants:conversation_participants(
          *,
          profile:profiles!conversation_participants_user_id_profiles_fkey(id, first_name, last_name, profile_photo_url)
        )
      `,
      )
      .eq('id', conversationId)
      .is('deleted_at', null)
      .single();

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    // Get current user ID
    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) {
      return Result.fail(
        new ServiceError('UNAUTHORIZED', 'User not authenticated'),
      );
    }

    // Collect profile photo paths and generate signed URLs
    const profilePhotoPaths = collectParticipantProfilePhotoPaths(
      data.participants || [],
    );
    const signedUrlMap = await batchGetProfilePhotoSignedUrls(
      this.client,
      profilePhotoPaths,
    );

    // Transform participants with signed URLs
    const participantsWithSignedUrls = enrichParticipantsWithSignedUrls(
      data.participants || [],
      signedUrlMap,
    );

    const conversation = {
      ...data,
      participants: participantsWithSignedUrls,
      my_participation:
        participantsWithSignedUrls.find(
          (p: { user_id: string }) => p.user_id === user.id,
        ) || null,
    } as ConversationWithDetails;

    return Result.ok(conversation);
  }

  /**
   * Get or create a direct conversation with another user
   * Uses the database function for atomic operation
   */
  async getOrCreateDirect(
    otherUserId: string,
  ): Promise<Result<{ conversationId: string }>> {
    const uuidValidation = this.validateUUID(otherUserId, 'otherUserId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data, error } = await this.client.rpc(
      'get_or_create_direct_conversation',
      {
        p_other_user_id: otherUserId,
      },
    );

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok({ conversationId: data as string });
  }

  /**
   * Create a new group conversation
   */
  async createGroup(
    params: CreateGroupConversationParams,
  ): Promise<Result<{ conversationId: string }>> {
    const { name, userIds, description } = params;

    // Validate inputs
    if (!name || name.trim().length === 0) {
      return Result.fail(
        new ServiceError('VALIDATION_ERROR', 'Group name is required'),
      );
    }

    if (!userIds || userIds.length === 0) {
      return Result.fail(
        new ServiceError(
          'VALIDATION_ERROR',
          'At least one other user is required for group',
        ),
      );
    }

    // Validate all UUIDs
    for (const userId of userIds) {
      const validation = this.validateUUID(userId, 'userId');
      if (validation.isFailure) {
        return Result.fail(validation.getError());
      }
    }

    const { data, error } = await this.client.rpc('create_group_conversation', {
      p_name: name.trim(),
      p_user_ids: userIds,
      p_description: description || undefined,
    });

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok({ conversationId: data as string });
  }

  /**
   * Update group conversation settings
   * Only owners/admins can update
   */
  async updateGroup(
    conversationId: string,
    params: { name?: string; description?: string; avatarUrl?: string },
  ): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const updateData: Database['public']['Tables']['conversations']['Update'] =
      {};
    if (params.name !== undefined) updateData.name = params.name.trim();
    if (params.description !== undefined)
      updateData.description = params.description;
    if (params.avatarUrl !== undefined)
      updateData.avatar_url = params.avatarUrl;

    if (Object.keys(updateData).length === 0) {
      return Result.ok(undefined);
    }

    const { error } = await this.client
      .from('conversations')
      .update(updateData)
      .eq('id', conversationId);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Leave a conversation (for groups)
   * Sets is_active = false on participant record
   */
  async leave(conversationId: string): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) {
      return Result.fail(
        new ServiceError('UNAUTHORIZED', 'User not authenticated'),
      );
    }

    const { error } = await this.client
      .from('conversation_participants')
      .update({
        is_active: false,
        left_at: new Date().toISOString(),
      })
      .eq('conversation_id', conversationId)
      .eq('user_id', user.id);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Mute/unmute a conversation
   */
  async setMuted(
    conversationId: string,
    isMuted: boolean,
    until?: Date,
  ): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) {
      return Result.fail(
        new ServiceError('UNAUTHORIZED', 'User not authenticated'),
      );
    }

    const { error } = await this.client
      .from('conversation_participants')
      .update({
        is_muted: isMuted,
        muted_until: until ? until.toISOString() : null,
      })
      .eq('conversation_id', conversationId)
      .eq('user_id', user.id);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Mark conversation as read
   * Uses database function to atomically update
   */
  async markAsRead(conversationId: string): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { error } = await this.client.rpc('mark_conversation_read', {
      p_conversation_id: conversationId,
    });

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Get total unread count across all conversations
   */
  async getTotalUnreadCount(): Promise<Result<number>> {
    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) {
      return Result.fail(
        new ServiceError('UNAUTHORIZED', 'User not authenticated'),
      );
    }

    const { data, error } = await this.client
      .from('conversation_participants')
      .select('unread_count')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .gt('unread_count', 0);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    const total = (data || []).reduce(
      (sum, p) => sum + (p.unread_count || 0),
      0,
    );
    return Result.ok(total);
  }
}
